import { LessonCard } from "@app/grades/components/LessonCard";
import { URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { cn } from "@design-system/lib/utils";
import { trans } from "@mongez/localization";
import { ArrowUpLeftIcon, BookOpenCheckIcon } from "lucide-react";
import type {
  GradeDetailsCategoryViewModel,
  GradeDetailsTheme,
} from "../../hooks/use-grade-details-page.hook";

type GradeChapterSectionProps = {
  item: GradeDetailsCategoryViewModel;
  theme: GradeDetailsTheme;
};

export function GradeChapterSection({
  item,
  theme,
}: GradeChapterSectionProps) {
  const { category, index, lessonCount, sectionId } = item;

  return (
    <section
      id={sectionId}
      className="scroll-mt-28 overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white shadow-[0_22px_70px_-52px_rgba(15,23,42,0.55)] dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="relative border-b border-slate-100 px-5 py-5 dark:border-slate-800 sm:px-7 sm:py-6">
        <div
          className={cn(
            "absolute inset-y-0 start-0 w-1.5 bg-gradient-to-b",
            theme.gradient,
          )}
        />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <span
                className={cn(
                  "flex size-14 items-center justify-center rounded-2xl text-xl font-black shadow-lg",
                  theme.badge,
                )}
              >
                {index}
              </span>
              <BookOpenCheckIcon className="absolute -bottom-1.5 -end-1.5 size-6 rounded-full border-2 border-white bg-white p-1 text-slate-700 dark:border-slate-900 dark:bg-slate-800 dark:text-slate-200" />
            </div>
            <div>
              <p
                className={cn(
                  "mb-1 text-xs font-bold uppercase tracking-wider",
                  theme.accent,
                )}
              >
                {trans("grades.categories")}
              </p>
              <h2 className="font-heading text-xl font-black text-slate-950 dark:text-white sm:text-2xl">
                {category.name}
              </h2>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 sm:text-sm">
                {trans("grades.lessonsInCategory", { count: lessonCount })}
              </p>
            </div>
          </div>

          <BaseLink
            to={URLS.grades.viewCategory(category)}
            className={cn(
              "inline-flex w-fit items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary-500",
              theme.soft,
            )}
          >
            {trans("viewMore")}
            <ArrowUpLeftIcon className="size-4 rtl:-rotate-90" />
          </BaseLink>
        </div>
      </div>

      <div className="grid gap-5 p-5 sm:grid-cols-2 sm:p-7 lg:grid-cols-3">
        {category.lessons.map(lesson => (
          <LessonCard
            key={lesson.id}
            lesson={lesson}
            className="h-full rounded-2xl border border-slate-100 shadow-none transition duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-xl dark:border-slate-700 dark:hover:border-primary-700"
          />
        ))}
      </div>
    </section>
  );
}

